import React from 'react';
import { cn } from '../lib/cn';
import { useModalKeys } from '../../../hooks/useModalKeys';

/* ─── Botão ─────────────────────────────────────────────────── */
type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'success' | 'soft';
type ButtonSize = 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 border border-transparent shadow-sm',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 border border-transparent shadow-sm',
  danger: 'bg-red-600 text-white hover:bg-red-700 border border-transparent shadow-sm',
  ghost: 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200',
  soft: 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-transparent'
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2'
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    />
  );
}

/* ─── Inputs ────────────────────────────────────────────────── */
export const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none ' +
  'transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 disabled:text-slate-500';

export const TextInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...props }, ref) {
    return <input ref={ref} className={cn(inputClass, className)} {...props} />;
  }
);

export function Select({ className, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn(inputClass, 'cursor-pointer', className)} {...props} />;
}

/* ─── Modal ─────────────────────────────────────────────────── */
/**
 * Backdrop dos modais do módulo. Segue a regra do painel (Fase 30b): NÃO
 * fecha no clique fora — só no botão ou no Esc — pra não perder dados
 * digitados por um clique acidental.
 */
export function ModalBackdrop({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useModalKeys(onClose);
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
      {children}
    </div>
  );
}

/* ─── Spinner ───────────────────────────────────────────────── */
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent', className)}
    />
  );
}

/* ─── Badges ────────────────────────────────────────────────── */
const TYPE_BADGE: Record<string, string> = {
  CTO: 'bg-red-100 text-red-700',
  CEP: 'bg-blue-100 text-blue-700',
  CEO: 'bg-emerald-100 text-emerald-700'
};

export function TypeBadge({ type }: { type: string }) {
  return (
    <span className={cn('inline-block rounded-md px-2 py-0.5 text-xs font-bold', TYPE_BADGE[type] || 'bg-slate-100 text-slate-600')}>
      {type}
    </span>
  );
}
